import React, { useState } from 'react';
import { Copy, Check, ShieldCheck, X, AlertTriangle } from 'lucide-react';
import { cn } from '../../utils/tailwindUtils';


/**
 * One-time-only credentials modal shown after a partner login is
 * provisioned or reset. The password is displayed exactly once — closing
 * the modal drops it from memory. Admin is expected to copy + share.
 *
 * Props:
 *   partnerName  — display name of the partner
 *   partnerKind  — 'doctor' | 'ambulance' | 'partner'
 *   credentials  — { created, username, password }
 *   onClose      — closer
 *   portalUrl    — optional link admin can share with the partner
 */
export default function PartnerCredentialsModal({
  partnerName, partnerKind = 'partner', credentials, onClose,
  portalUrl = '/partner-portal/login',
}) {
  const [copiedField, setCopiedField] = useState(null);

  const copy = (label, value) => {
    if (!value) return;
    navigator.clipboard?.writeText(value);
    setCopiedField(label);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const copyAll = () => {
    const text = `Portal URL: ${window.location.origin}${portalUrl}
Username: ${credentials.username}
Password: ${credentials.password}`;
    navigator.clipboard?.writeText(text);
    setCopiedField('all');
    setTimeout(() => setCopiedField(null), 1500);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white flex items-start justify-between">
          <div className="flex items-start gap-2">
            <ShieldCheck className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-semibold">
                {credentials.created ? 'Portal login created' : 'Portal password reset'}
              </h2>
              <p className="text-[11px] opacity-90 mt-0.5">
                for {partnerName || `this ${partnerKind}`}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-[11px] text-amber-800 dark:text-amber-300">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <p>
              Copy the password now — it won't be shown again. If lost, generate a new one from the partner's detail page.
            </p>
          </div>

          <Field
            label="Portal URL"
            value={`${window.location.origin}${portalUrl}`}
            copiedField={copiedField}
            onCopy={copy}
            copyKey="url"
          />
          <Field
            label="Username"
            value={credentials.username}
            copiedField={copiedField}
            onCopy={copy}
            copyKey="username"
            mono
          />
          {credentials.password && (
            <Field
              label="Password"
              value={credentials.password}
              copiedField={copiedField}
              onCopy={copy}
              copyKey="password"
              mono
              highlight
            />
          )}

          <button
            onClick={copyAll}
            className={cn(
              'w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              copiedField === 'all'
                ? 'bg-green-500 text-white'
                : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800/60',
            )}
          >
            {copiedField === 'all'
              ? <><Check className="w-4 h-4" /> All copied to clipboard</>
              : <><Copy className="w-4 h-4" /> Copy all (URL + username + password)</>}
          </button>
        </div>

        <div className="p-3 border-t border-gray-100 dark:border-gray-700 flex justify-end">
          <button onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}


function Field({ label, value, copiedField, onCopy, copyKey, mono, highlight }) {
  const isCopied = copiedField === copyKey;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium mb-1">{label}</div>
      <div className={cn(
        'flex items-center gap-2 rounded-lg border px-2.5 py-1.5',
        highlight
          ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
          : 'bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-700',
      )}>
        <code className={cn('flex-1 truncate text-sm', mono ? 'font-mono' : '', highlight ? 'font-bold text-emerald-700 dark:text-emerald-300' : 'text-gray-900 dark:text-white')}>
          {value}
        </code>
        <button onClick={() => onCopy(copyKey, value)}
          className={cn('p-1 rounded transition-colors flex-shrink-0',
            isCopied ? 'text-green-600' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300')}>
          {isCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}
